import { BookConstants } from '@/constants/bookConstants';
import type {
  BookThicknessInput,
  BookThickness,
  HiddenThicknessInput,
  ThicknessLineInsetInput,
  ThicknessSide
} from '@/types/book';

const { MAX_THICKNESS_RATIO, THICKNESS_LINE_WIDTH } = BookConstants;

/** 由页面宽度计算厚度线数上限。 */
export function getMaxThicknessLineCount(pageWidth: number): number {
  return Math.max(0, Math.floor(pageWidth * MAX_THICKNESS_RATIO));
}

/**
 * 厚度像素宽 → 线数。
 * 每条线固定 THICKNESS_LINE_WIDTH px，不把多页挤进更窄宽度。
 */
export function getThicknessLineCount(thicknessWidth: number): number {
  if (THICKNESS_LINE_WIDTH <= 0) return 0;
  return Math.max(0, Math.round(thicknessWidth / THICKNESS_LINE_WIDTH));
}

/** 页数 → 线数：每页一条线，超出上限则截断线数。 */
function pagesToLineCount(pageCount: number, maxLineCount: number): number {
  return Math.min(Math.max(0, Math.floor(pageCount)), Math.max(0, maxLineCount));
}

/** 线数 → 厚度像素宽（线数 × 固定线宽）。 */
function lineCountToThicknessWidth(lineCount: number): number {
  return Math.max(0, lineCount) * THICKNESS_LINE_WIDTH;
}

/** 根据当前页码和展示模式计算书本左右两侧的页堆厚度（像素宽）。 */
export function calculateBookThickness(input: BookThicknessInput): BookThickness {
  const maxLineCount = getMaxThicknessLineCount(input.pageWidth);
  const toWidth = (pageCount: number) =>
    lineCountToThicknessWidth(pagesToLineCount(pageCount, maxLineCount));

  if (input.mode === 'single') {
    const remainingPages = Math.max(0, input.pageCount - input.currentPageIndex);

    return {
      left: 0,
      right: toWidth(remainingPages)
    };
  }

  const leftPages = Math.max(0, input.currentPageIndex + 1);
  const rightPages = Math.max(0, input.pageCount - input.currentPageIndex - 1);

  return {
    left: toWidth(leftPages),
    right: toWidth(rightPages)
  };
}

/** 翻到封面或封底空白侧时，判断哪一侧厚度需要临时隐藏。 */
export function getHiddenThicknessSide(input: HiddenThicknessInput): ThicknessSide | null {
  if (input.mode === 'double') {
    if (input.direction === 'back' && input.bottomPageIndex < 0) return 'left';
    if (input.direction === 'forward' && input.bottomPageIndex >= input.pageCount) return 'right';
  }

  return null;
}

/** 计算厚度线条上下缩进，让页堆边缘呈现轻微弧度。 */
export function getThicknessLineInset(input: ThicknessLineInsetInput): number {
  if (input.lineCount <= 1) return 0;

  const progress = Math.max(0, Math.min(1, input.lineIndex / (input.lineCount - 1)));
  const thicknessRatio = Math.max(
    0,
    Math.min(1, input.lineCount / Math.max(1, input.maxLineCount))
  );
  const maxInset = input.pageHeight * 0.02 * thicknessRatio;

  return progress * maxInset;
}
